import type { QueryClient } from "@tanstack/react-query";

import { projectPreparedSettingsPatch } from "./project-prepared-patch";
import type { SettingsApplyOutcome } from "./settings-native-approval";
import {
  appliedSettingsTargetIds,
  isKnownSettingsTarget,
  settingsTargetQueryKeys,
  settingsTargetsRefreshCurrentUser,
  settingsTargetsRefreshTenant,
} from "./settings-target-query-keys";
import type { PreparedSettingsPatchPayload } from "./types";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";

const refreshes = new Map<string, Promise<void>>();
export function refreshSettingsOutcome({
  client,
  tenant,
  epoch,
  executionId,
  patch,
  outcome,
  refetchUser,
  refetchTenant,
}: {
  client: QueryClient;
  tenant?: string;
  epoch: number;
  executionId: string;
  patch: PreparedSettingsPatchPayload;
  outcome: SettingsApplyOutcome;
  refetchUser: () => Promise<unknown>;
  refetchTenant: () => Promise<unknown>;
}) {
  if (outcome.kind === "failed") return Promise.resolve();
  const key = `${tenant}:${epoch}:${executionId}`;
  const existing = refreshes.get(key);
  if (existing) return existing;
  const model = projectPreparedSettingsPatch(patch);
  const changedFields = new Set(
    outcome.result.appliedChanges?.map((change) => change.fieldId),
  );
  const ids = model.changes
    .filter((change) =>
      change.units.some((unit) => changedFields.has(unit.fieldId ?? "")),
    )
    .flatMap((change) => change.unitIds);
  const targets = outcome.appliedTargetIds.length
    ? outcome.appliedTargetIds
    : appliedSettingsTargetIds(
        model,
        ids.length ? ids : model.changes.flatMap((change) => change.unitIds),
      );
  useAskDecodaStore.getState().setSettingsRefreshing(patch.patchId, true);
  const work = Promise.resolve()
    .then(async () => {
      if (useAskDecodaStore.getState().runtimeEpoch !== epoch) return;
      if (outcome.kind !== "rejected") {
        if (targets.some((target) => !isKnownSettingsTarget(target)))
          await client.invalidateQueries({}, { throwOnError: true });
        else
          await Promise.all(
            settingsTargetQueryKeys(targets).map((queryKey) =>
              client.invalidateQueries({ queryKey }, { throwOnError: true }),
            ),
          );
        await Promise.all([
          settingsTargetsRefreshCurrentUser(targets) ? refetchUser() : null,
          settingsTargetsRefreshTenant(targets) ? refetchTenant() : null,
        ]);
      }
      await client.invalidateQueries(
        { queryKey: ["settings-patch-lifecycle", patch.patchId] },
        { throwOnError: true },
      );
    })
    .catch(() => {
      if (useAskDecodaStore.getState().runtimeEpoch === epoch)
        useAskDecodaStore
          .getState()
          .setSettingsRefreshWarning(
            patch.patchId,
            outcome.kind === "rejected"
              ? "The patch was rejected, but its local status could not be refreshed. Refresh the page."
              : "Changes were committed, but local settings could not be refreshed. Refresh the page.",
          );
    })
    .finally(() => {
      if (useAskDecodaStore.getState().runtimeEpoch === epoch)
        useAskDecodaStore
          .getState()
          .setSettingsRefreshing(patch.patchId, false);
    });
  refreshes.set(key, work);
  return work;
}
